import { format, formatDistanceToNow, parseISO } from 'date-fns';

/**
 * Format a price in pence to a display string
 * @param pence Price in pence (e.g., 145.9)
 * @returns Formatted string like "145.9p"
 */
export function formatPrice(pence: number): string {
  return `${pence.toFixed(1)}p`;
}

/**
 * Format a price in pence to pounds and pence
 * @param pence Price in pence (e.g., 145.9)
 * @returns Formatted string like "£1.46"
 */
export function formatPricePounds(pence: number): string {
  const pounds = pence / 100;
  return `£${pounds.toFixed(2)}`;
}

/**
 * Format an ISO timestamp to a readable date
 * @param isoString ISO timestamp string
 * @returns Formatted string like "3 Feb 2026"
 */
export function formatDate(isoString: string): string {
  return format(parseISO(isoString), 'd MMM yyyy');
}

/**
 * Format an ISO timestamp to a readable time
 * @param isoString ISO timestamp string
 * @returns Formatted string like "10:30"
 */
export function formatTime(isoString: string): string {
  return format(parseISO(isoString), 'HH:mm');
}

/**
 * Format an ISO timestamp to a readable date and time
 * @param isoString ISO timestamp string
 * @returns Formatted string like "3 Feb 2026, 10:30"
 */
export function formatDateTime(isoString: string): string {
  return format(parseISO(isoString), 'd MMM yyyy, HH:mm');
}

/**
 * Format an ISO timestamp to relative time
 * @param isoString ISO timestamp string
 * @returns Formatted string like "2 hours ago"
 */
export function formatRelativeTime(isoString: string): string {
  return formatDistanceToNow(parseISO(isoString), { addSuffix: true });
}

/**
 * Format an opening time string for display
 * @param time Time string like "06:00" or "06:00:00"
 * @returns Formatted string like "6am" or "10:30am"
 */
export function formatOpeningTime(time: string): string {
  const [hours, minutes] = time.split(':').map(Number);
  const hour = hours ?? 0;
  const minute = minutes ?? 0;
  const period = hour >= 12 ? 'pm' : 'am';
  const displayHour = hour % 12 || 12;
  
  if (minute === 0) {
    return `${displayHour}${period}`;
  }
  return `${displayHour}:${minute.toString().padStart(2, '0')}${period}`;
}

/**
 * Format a full address for display
 */
export function formatAddress(address: {
  line1: string;
  line2: string | null;
  city: string;
  county: string;
  postcode: string;
}): string {
  const parts = [
    address.line1,
    address.line2,
    address.city,
    address.county,
    address.postcode,
  ].filter(Boolean);
  
  return parts.join(', ');
}

/**
 * Format a short address for display
 */
export function formatShortAddress(address: {
  line1: string;
  city: string;
  postcode: string;
}): string {
  return `${address.line1}, ${address.postcode}`;
}
